import SEO from "@/components/SEO";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="mb-2 font-display text-lg font-bold text-navy">{title}</h2>
    <div className="space-y-2 leading-relaxed text-gray-600">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="ml-5 list-disc space-y-1.5 text-gray-600">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export default function Terms() {
  return (
    <>
      <SEO title="Terms & Conditions | Happy Holidays Wale" description="Terms and conditions for booking domestic and international tour packages with Happy Holidays Wale." />
      <div className="mx-auto max-w-3xl px-4 py-14 md:px-8">
        <h1 className="font-display text-4xl font-extrabold text-navy">Terms &amp; Conditions</h1>
        <p className="mt-2 text-sm text-gray-400">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>

        <div className="mt-6 rounded-2xl bg-brandorange/10 p-5 text-gray-700">
          <p className="font-semibold text-navy">Congratulations on choosing Happy Holidays Wale for your journey! 🌍✈️</p>
          <p className="mt-2">We are excited to be a part of your travel plans. To ensure complete transparency, smooth coordination, and a pleasant experience for all travelers, we request you to carefully read and acknowledge the following Terms &amp; Conditions before confirming your booking.</p>
        </div>

        <div className="mt-8">
          <Section title="1. Airline Policy & Flight Operations">
            <p>Happy Holidays Wale acts only as a facilitator between the traveler and the airline.</p>
            <List items={[
              "Any matters related to flight delay, rescheduling, cancellation, route change, aircraft change, date change, or airline updates are entirely governed by the respective airline's policy.",
              "Happy Holidays Wale holds no control or responsibility over airline decisions or operational changes.",
              "Seat selection, meal preference, and seat allocation are completely at the airline's discretion and are not in the control of Happy Holidays Wale, even after payment.",
            ]} />
          </Section>

          <Section title="2. Hotel Policy & Accommodation">
            <List items={[
              "Hotel booking confirmations are subject to availability at the time of confirmation.",
              "Hotel check-in and check-out timings are strictly governed by international hotel policies.",
              "Room category, room view, bed type, and floor allocation are subject to availability and hotel discretion at the time of check-in.",
              "Happy Holidays Wale is not responsible for any hotel overbooking, category change, or last-minute operational adjustments made by the hotel.",
            ]} />
          </Section>

          <Section title="3. Cancellation & Refund Policy">
            <p className="font-semibold text-navy">Before 30 days of travel:</p>
            <List items={[
              "Only the land package portion (excluding flights, hotels, visas, insurance, and third-party services) may be eligible for refund.",
              "Airline, hotel, and visa charges are non-refundable, as per supplier policies.",
              "Happy Holidays Wale will make best possible efforts to recover eligible amounts wherever feasible.",
            ]} />
            <p className="mt-2 font-semibold text-navy">Within 30 days of travel or after departure:</p>
            <List items={[
              "No refund will be applicable under any circumstances.",
              "Refund timelines, if applicable, are subject to supplier processing time and banking procedures.",
            ]} />
          </Section>

          <Section title="4. Government Taxes (TCS – Tax Collected at Source)">
            <List items={[
              "As per Government of India regulations, 5% TCS is applicable on international tour packages.",
              "TCS is mandatory and will be charged as per prevailing government norms.",
              "The collected TCS amount is refundable/adjustable while filing your Income Tax Return (ITR), as per individual eligibility.",
            ]} />
          </Section>

          <Section title="5. Immigration & Entry Clearance">
            <List items={[
              "Immigration clearance is solely at the discretion of the destination country's immigration authorities.",
              "Holding a valid visa does not guarantee entry into any country.",
            ]} />
            <p className="mt-2">Travelers must carry all required documents, including:</p>
            <List items={[
              "Valid passport and visa",
              "Return/onward tickets",
              "Hotel confirmations",
              "Proof of funds",
              "Any additional documents requested by immigration authorities",
            ]} />
            <p className="mt-2">Any denial of entry, questioning, delay, offloading, or deportation is beyond Happy Holidays Wale's control, and no refund will be applicable in such cases.</p>
          </Section>

          <Section title="6. Baggage Policy">
            <List items={[
              "Baggage allowance, size, and weight limits are strictly as per airline policy.",
              "Extra baggage charges, if any, must be paid directly to the airline by the traveler.",
              "Happy Holidays Wale is not responsible for lost, delayed, or damaged baggage.",
            ]} />
          </Section>

          <Section title="7. Health, Fitness & Travel Readiness">
            <List items={[
              "International travel may involve extensive walking, long flights, time-zone changes, and climate variations.",
              "Travelers are responsible for ensuring they are medically fit to travel.",
              "Any pre-existing medical condition must be declared prior to booking.",
              "Happy Holidays Wale is not responsible for any health-related issues, accidents, or medical emergencies during the tour.",
            ]} />
          </Section>

          <Section title="8. Itinerary Changes & Tour Operations">
            <p>Tour itineraries are planned carefully; however, changes may occur due to:</p>
            <List items={["Weather conditions", "Local regulations", "Airline or supplier issues", "Safety or operational reasons"]} />
            <p className="mt-2">Happy Holidays Wale reserves the right to modify, rearrange, or substitute itinerary components without prior notice for the safety and comfort of travelers. No refund shall be provided for missed sightseeing or activities due to personal delays, health issues, or non-cooperation.</p>
          </Section>

          <Section title="9. Tour Cancellation by Traveler">
            <List items={[
              "If a traveler cancels or leaves the tour midway for personal reasons, no refund or compensation shall be applicable.",
              "Unused services such as hotel stays, meals, transfers, or sightseeing are non-refundable.",
            ]} />
          </Section>

          <Section title="10. Responsibility & Liability">
            <p>Happy Holidays Wale shall not be responsible for:</p>
            <List items={["Natural calamities", "Political unrest", "Strikes", "Acts of God", "Government restrictions", "Situations beyond human control"]} />
            <p className="mt-2">Any additional expenses arising due to such circumstances shall be borne by the traveler directly.</p>
          </Section>

          <Section title="11. Final Acknowledgement">
            <p>By proceeding with the booking, the traveler confirms that:</p>
            <List items={[
              "All Terms & Conditions have been read, understood, and accepted.",
              "The traveler agrees to comply with airline, hotel, immigration, and local laws.",
              "No claims shall be made against Happy Holidays Wale for matters beyond its control.",
            ]} />
          </Section>
        </div>
      </div>
    </>
  );
}
